import { defineComponent as _defineComponent } from 'vue'
import Reservation, { ReservationStatus } from '@/model/Reservation';
import {
  computed, onBeforeUnmount, PropType, Ref, ref,
} from 'vue';
import moment from 'moment';
import { contrastForColor, darkenColor } from '@/services/color-utils';
import { useVuetify } from '@/components/helpers/vue2-helpers';
import TimeTableStripedFill from './TimeTableStripedFill.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'TimeTableEvent',
  props: {
  reservation: { type: Object as PropType<Reservation>, required: true },
  slotWidth: { type: Number, required: true },
  rowHeight: { type: Number, required: true },
  rowSpan: { type: Number, required: false, default: 1 },
  timelineStart: { type: Date, required: true },
  selected: { type: Boolean, required: false, default: false },
  overbooked: { type: Boolean, required: false, default: false },
},
  emits: ['show-tooltip', 'hide-tooltip', 'context-menu', 'click-event'],
  setup(__props, { emit }) {

const props = __props;

const $vuetify = useVuetify();


const root = ref(null) as Ref<null | HTMLElement>;


const gapColor = '#D6D6D8';
const gapBorderColor = '#858593';
const gapOpacity = '0.6';

onBeforeUnmount(() => ((root.value as HTMLElement).classList.remove('tt-event')));

const hoverTimeout = ref(undefined) as Ref<undefined | number>;

// relative pos from left, difference between timeline and res. start
const left = computed(() => {
  const startTime = moment(props.reservation.dateBegin);
  const minutesFromStart = moment.duration(startTime.diff(props.timelineStart)).asMinutes();
  const offsetFromStart = (minutesFromStart / 30) * props.slotWidth;
  return offsetFromStart;
});

// + 1 is compensation for border, the divs are aligned over the border lines
const height = computed(() => props.rowHeight * props.rowSpan + 1);

const width = computed(() => {
  const startTime = moment(props.reservation.dateBegin);
  const endTime = moment(props.reservation.dateEnd);
  const duration = moment.duration(endTime.diff(startTime)).asMinutes();
  return (duration / 30) * props.slotWidth;
});

// gap between reservations, no gap shown for block
const reservationGap = computed(
  () => (props.reservation.isBlock ? 0 : ((props.reservation.tab?.bookingCap || 0) / 30) * props.slotWidth),
);

const isNarrowWidth = computed(() => width.value <= 100);
const isMovementBlocked = computed(() => props.reservation.partySize === 0 || props.reservation.holdTabItems);
const isBlock = computed(() => props.reservation.isBlock);
const hasTabItems = computed(() => props.reservation.tabItems.find((o) => o.isDummy) === undefined);

// Colors
const fillColor = computed(() => {
  if (isBlock.value) {
    return $vuetify.theme.dark ? '#666668' : '#C6C6C8';
  }

  if (props.overbooked || !hasTabItems.value) {
    return props.selected ? darkenColor('#FF002B', 51) : '#FF002B'; // error
  }

  let color = '#3355FF';
  switch (props.reservation.status) {
    case ReservationStatus.CheckedIn: color = '#FF8262'; break;// 'orange';
    case ReservationStatus.Seated: color = '#00A26F'; break; // 'green';
    case ReservationStatus.NonConfirmed: color = '#E42DD0'; break; // 'purple';
    case ReservationStatus.CheckedOut: color = '#858593'; break; // gray-50
    default: break;
  }
  return props.selected ? darkenColor(color, 51) : color;
});

const strokeColor = computed(() => (isBlock.value ? '#3F3F49' : darkenColor(fillColor.value, 51)));

const bgOpacity = computed(() => (props.overbooked ? 0.8 : 1));

const fill = computed(
  () => (isMovementBlocked.value ? `url(#tt-pattern-stripe-${props.reservation.id})` : fillColor.value),
);

// Chips
const colorForLabel = computed(() => {
  if (props.reservation.label?.color === fillColor.value) return '#FFFFFF';
  return props.reservation.label?.color;
});

// paths to draw the event
const partysizeBgPath = computed(() => {
  const p = 2; const r = 4; const w = 24;
  const h = height.value - p * 2 - 1;
  return `M${r + p} ${p}
          h${w - r / 2}
          v${h}
          h-${w - r / 2}
          a${r},${r} 0,0,1 -${r},-${r}
          v-${h - r * 2}
          a${r},${r} 0,0,1 ${r},-${r}`;
});

const bgPath = computed(() => {
  const p = 2;
  const r = isNarrowWidth.value ? 2 : 4;
  const capLimit = Math.max(9, Math.min(15 * props.rowSpan, (width.value / 5)));
  const cp = isNarrowWidth.value ? 4 : 7; // control point for quadcurve
  const h = height.value - p * 2 - 1;
  const w = width.value - 1;

  return `M${r + p} ${p} H${w - capLimit}
              q${cp},0 ${capLimit} ${h / 2}
              q-${capLimit - cp},${h / 2} -${capLimit} ${h / 2}
              h-${w - r - capLimit - p}
              a${r},${r} 0,0,1 -${r},-${r}
              v-${h - r * 2}
              a${r},${r} 0,0,1 ${r},-${r}`;
});

const bgPathGap = computed(() => {
  const p = 2;
  const r = isNarrowWidth.value ? 2 : 4;
  const capLimit = Math.max(9, Math.min(15 * props.rowSpan, (width.value / 5)));
  const cp = isNarrowWidth.value ? 4 : 7; // control point for quadcurve
  const h = height.value - p * 2 - 1;
  const w = width.value + reservationGap.value - 1;

  return `M${r + p} ${p} H${w - capLimit}
              q${cp},0 ${capLimit} ${h / 2}
              q-${capLimit - cp},${h / 2} -${capLimit} ${h / 2}
              h-${w - r - capLimit - p}
              a${r},${r} 0,0,1 -${r},-${r}
              v-${h - r * 2}
              a${r},${r} 0,0,1 ${r},-${r}`;
});

// Mouse Handlers
function mouseEnter(evt: MouseEvent) {
  if (evt.buttons !== 0) { return; }
  window.clearTimeout(hoverTimeout.value);
  hoverTimeout.value = window.setTimeout(() => {
    emit('show-tooltip', root.value);
  }, 700);
}
function mouseMove(evt: MouseEvent) {
  if (evt.buttons !== 0) { return; }
  mouseEnter(evt);
}
function mouseLeave() {
  emit('hide-tooltip');
  window.clearTimeout(hoverTimeout.value);
}
function onRightClick(e: MouseEvent) {
  // do not trigger right click if it originated on touch device (long touch == right click)
  // as this will interfere with drag and drop
  if (e.buttons === 0) { return; }
  emit('context-menu', props.reservation, e);
}
function onClick(e: any) {
  // see drag and drop mixin for explanation of this
  const element = root.value as HTMLElement;
  if (element.dataset.dnd === '1') {
    element.dataset.dnd = '0';
    return;
  }

  emit('click-event', props.reservation, e);
}


return { __sfc: true,$vuetify, props, root, emit, gapColor, gapBorderColor, gapOpacity, hoverTimeout, left, height, width, reservationGap, isNarrowWidth, isMovementBlocked, isBlock, hasTabItems, fillColor, strokeColor, bgOpacity, fill, colorForLabel, partysizeBgPath, bgPath, bgPathGap, mouseEnter, mouseMove, mouseLeave, onRightClick, onClick, contrastForColor, darkenColor, TimeTableStripedFill }
}

})